/**
 * Utilities for sharing calculator state via URL parameters
 */

// Parameter key mapping to keep URL compact
const PARAM_KEYS: Record<keyof CalculatorState, string> = {
  baseSalary: "bs",
  targetCommission: "tc",
  commissionRate: "cr",
  rampMonths1to3: "r1",
  rampMonths4to6: "r2",
  rampMonths7to9: "r3",
  rampMonths10to12: "r4",
  guaranteeMonths1to3: "g1",
  guaranteeMonths4to6: "g2",
  accelerators: "acc", // Accelerators array as a single parameter
  monthlyPerformances: "mp",
};

// Reverse lookup for parameter keys
const REVERSE_PARAM_KEYS: Record<string, keyof CalculatorState> =
  Object.entries(PARAM_KEYS).reduce(
    (acc, [key, value]) => ({ ...acc, [value]: key }),
    {},
  );

/**
 * Interface for calculator state
 */
export interface CalculatorState {
  baseSalary: number;
  targetCommission: number;
  commissionRate: number;
  rampMonths1to3: number;
  rampMonths4to6: number;
  rampMonths7to9: number;
  rampMonths10to12: number;
  guaranteeMonths1to3: number;
  guaranteeMonths4to6: number;
  accelerators: { threshold: number; multiplier: number }[];
  monthlyPerformances: number[];
}

/**
 * Serializes calculator state to URL parameters
 */
export function serializeStateToParams(
  state: Partial<CalculatorState>,
): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(state)) {
    const paramKey = PARAM_KEYS[key as keyof CalculatorState];

    if (paramKey && value !== undefined) {
      if (key === "accelerators" && Array.isArray(value)) {
        // Create a compact string representation of accelerators
        // Format: threshold1:multiplier1,threshold2:multiplier2,...
        const acceleratorsString = value
          .map((acc) =>
            typeof acc === "object" && "threshold" in acc && "multiplier" in acc
              ? `${acc.threshold}:${acc.multiplier}`
              : "",
          )
          .join(",");

        params.set(paramKey, acceleratorsString);
      }
      // Handle regular arrays (monthlyPerformances)
      else if (Array.isArray(value)) {
        params.set(paramKey, value.join(","));
      }
      // Handle primitive values
      else {
        params.set(paramKey, value.toString());
      }
    }
  }

  return params.toString();
}

/**
 * Deserializes URL parameters to calculator state
 */
export function deserializeParamsToState(
  queryString: string,
): Partial<CalculatorState> {
  const params = new URLSearchParams(queryString);
  const state: Partial<CalculatorState> = {};

  for (const [paramKey, value] of params.entries()) {
    const stateKey = REVERSE_PARAM_KEYS[paramKey];

    if (stateKey) {
      // Parse accelerators array
      if (stateKey === "accelerators") {
        try {
          // Format: threshold1:multiplier1,threshold2:multiplier2,...
          state[stateKey] = value.split(",").map((pair) => {
            const [threshold, multiplier] = pair.split(":").map(Number);
            return { threshold, multiplier };
          });
        } catch {
          // In case of parsing error, provide a default accelerator
          state[stateKey] = [{ threshold: 100, multiplier: 1.5 }];
        }
      }
      // Handle monthlyPerformances array
      else if (stateKey === "monthlyPerformances") {
        state[stateKey] = value.split(",").map(Number);
      }
      // Handle primitive numeric values
      else {
        state[stateKey] = Number(value);
      }
    }
  }

  return state;
}
